import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiGet, uiHeader, uiKpis, uiMoney, uiPill } from "../ui/kit.js";
import { toast } from "../ui/modals.js";

/**
 * Global Holdings (was "Country Portfolio"): everything your company holds
 * outside its own business lines, behind two buttons:
 *   🌍 Countries          this file (backend/country_portfolio.py)
 *   🏢 Companies you own  views/holdings.js (backend/holdings.py): M&A buys,
 *                         stakes, startups, with each industry's moves
 * window.__ghSeg picks the half; window.__ghOpen opens a company.
 *
 * Countries: every country you operate in, as an investment.
 *
 * Left: your countries, each with revenue, profit after tax, return on what
 * you invested and a verdict (invest more / hold / review). Right: the open
 * country: the money in and out, the market, your incentives and payments,
 * the government's offer at every investment size (a slider with a live
 * quote), and every move: expand local operations, a marketing push, a
 * country manager, a local plant, CSR, localize a product, hedge the
 * currency, visit the government, or leave.
 * A deal signed in Government Relations opens here (window.__cpSelect).
 */

const cpM = (v) => uiMoney(v, { compact: true });
let cpSel = null;
let ghSeg = "countries";
const GH_SEGS = [["countries", "🌍 Countries"], ["companies", "🏢 Companies you own"]];

async function cpExec(type, params) {
  try { toast((await postJSON("/actions/execute", { action: { type, params } })).message); return true; }
  catch (e) { toast(e.message, true); return false; }
}

const cpPct = (v, d = 1) => (v === null || v === undefined ? "—" : `${v.toFixed(d)}%`);
const VERDICT_TONE = { "Invest more": "good", Hold: "info", Review: "warn", Home: "" };

function cpRow(r, on) {
  return `<button type="button" class="cp-row ${on ? "on" : ""} ${r.just_signed ? "fresh" : ""}" data-cp="${esc(r.country)}">
    <span class="cp-row-name"><b>${esc(r.country)}</b><em>${r.is_hq ? "Headquarters" : `${r.share_pct}% of revenue · ${esc(r.currency)}`}</em></span>
    <span class="cp-row-num"><em>Revenue/mo</em><b>${cpM(r.revenue)}</b></span>
    <span class="cp-row-num"><em>After tax/mo</em><b class="${r.profit_after_tax >= 0 ? "pos" : "neg"}">${cpM(r.profit_after_tax)}</b></span>
    <span class="cp-row-num"><em>Return</em><b>${r.is_hq ? "—" : cpPct(r.roi_pct, 0)}</b></span>
    ${r.just_signed ? uiPill("just signed", "good") : uiPill(r.verdict, VERDICT_TONE[r.verdict])}</button>`;
}

function cpStat(label, value, tone = "", sub = "") {
  return `<div class="cp-stat"><span>${esc(label)}</span><b class="${tone}">${value}</b>${sub ? `<em>${sub}</em>` : ""}</div>`;
}

function cpDetail(d) {
  const parts = [["government", "Government deals"], ["entry", "Market entry"], ["expansion", "Expansions"], ["local", "Local operations"]]
    .filter(([k]) => d.invested_parts[k] > 0);
  const inTotal = d.invested || 1;
  const cash = d.cash;
  const slideMax = (frac, min) => Math.max(min, Math.round((cash * frac) / 10000) * 10000);
  return `
    <div class="cp-head"><div><span class="sx-eyebrow">${d.is_hq ? "Headquarters" : "Country you operate in"} · ${esc(d.currency)}</span><h3>${esc(d.country)}</h3></div>
      <div class="cp-head-pills">${d.just_signed ? uiPill("just signed: decide your next move", "good") : ""}${uiPill(d.verdict, VERDICT_TONE[d.verdict])}</div></div>

    <h4 class="cp-h">Money out of ${esc(d.country)} <span>a month</span></h4>
    <div class="cp-stats">
      ${cpStat("Revenue", cpM(d.revenue), "", `${d.share_pct}% of your sales`)}
      ${cpStat("Operating profit", cpM(d.op_profit), d.op_profit >= 0 ? "pos" : "neg", `${d.margin_pct}% margin`)}
      ${cpStat("Tax", `${d.effective_tax_pct}%`, d.tax_holiday ? "pos" : "", d.tax_holiday ? `holiday (normally ${d.tax_pct}%)` : "corporate rate")}
      ${cpStat("Profit after tax", cpM(d.profit_after_tax), d.profit_after_tax >= 0 ? "pos" : "neg", `${cpM(d.profit_after_tax * 12)} a year`)}
    </div>

    ${d.is_hq ? "" : `<h4 class="cp-h">Money into ${esc(d.country)}</h4>
    <div class="cp-stats">
      ${cpStat("Invested so far", cpM(d.invested))}
      ${cpStat("Return a year", cpPct(d.roi_pct, 1), d.roi_pct === null ? "" : d.roi_pct >= 0 ? "pos" : "neg", "profit after tax ÷ invested")}
      ${cpStat("Pays back in", d.payback_years ? `${d.payback_years} yrs` : "—")}
      ${cpStat("Still owed", cpM(d.due), d.due > 0 ? "warn" : "", "government instalments")}
    </div>
    ${parts.length ? `<div class="cp-split">${parts.map(([k], i) => `<i style="flex:${d.invested_parts[k] / inTotal};background:${["#7ee8fa", "#a78bfa", "#34d399", "#fbbf24"][i]}"></i>`).join("")}</div>
      <div class="cp-legend">${parts.map(([k, l], i) => `<span><i style="background:${["#7ee8fa", "#a78bfa", "#34d399", "#fbbf24"][i]}"></i>${l} <b>${cpM(d.invested_parts[k])}</b></span>`).join("")}</div>` : ""}`}

    <h4 class="cp-h">The market</h4>
    <div class="cp-stats">
      ${cpStat("Economy", `$${d.gdp_bn >= 1000 ? (d.gdp_bn / 1000).toFixed(1) + "T" : d.gdp_bn + "B"}`, "", `growing ${d.gdp_growth_pct}% a year`)}
      ${cpStat("Your market there", cpM(d.market), "", `you have ${d.market_share_pct}% of it`)}
      ${cpStat("Inflation · rates", `${d.inflation_pct}% · ${d.interest_pct}%`)}
      ${cpStat("Trust in you", `${Math.round(d.trust)}/100`, d.trust >= 60 ? "pos" : d.trust < 40 ? "neg" : "")}
    </div>
    ${d.risks.length ? `<div class="cp-risks">${d.risks.map((r) => `<div class="cp-risk lv${r.level}" title="${esc(r.why)}"><b>${esc(r.label)}</b><span>${esc(r.word)}</span></div>`).join("")}</div>` : ""}
    <div class="cp-tags">
      ${d.localized.length ? d.localized.map((p) => `<span>📦 ${esc(p)}</span>`).join("") : `<span class="dim">No product localized here yet</span>`}
      ${d.plant ? `<span>🏭 Local plant</span>` : ""}${d.hedged ? `<span>🛡 ${esc(d.currency)} hedged</span>` : ""}
      ${d.marketing_until ? `<span>📣 Campaign to month ${d.marketing_until}</span>` : ""}${d.manager_name ? `<span>👤 ${esc(d.manager_name)}, manager to month ${d.manager_until}</span>` : ""}
    </div>

    ${d.incentives.length ? `<h4 class="cp-h">Your agreements with the government</h4>
      <ul class="cp-deals">${d.incentives.map((x) => `<li><b>${esc(x.words)}</b><span>${esc(x.stage)} · month ${x.month} · you committed ${cpM(x.invest)}</span>
        ${x.earned ? `<span class="cp-earned">📈 Earned so far: ${esc(x.earned)}${x.status === "running" && x.months_left ? ` · ${x.months_left} months left` : x.status ? ` · ${esc(x.status)}` : ""}</span>` : ""}</li>`).join("")}</ul>` : ""}

    ${d.is_hq || !d.offers.length ? "" : `<h4 class="cp-h">Invest more with the government <span>the more you put in, the more they give</span></h4>
    <div class="cp-tiers">${d.offers.map((o) => `<button type="button" class="cp-tier" data-tier="${o.amount}"><em>${esc(o.tier)}</em><b>${cpM(o.amount)}</b><span>${esc(o.words || "")}</span><i>${Math.round(o.probability * 100)}% full sign-off</i></button>`).join("")}</div>
    <label class="iv-slide">How much to invest <b id="cp-gov-amt"></b><input type="range" id="cp-gov" min="50000" max="${slideMax(2.5, 200000)}" step="10000" value="${Math.min(slideMax(2.5, 200000), d.offers[1] ? d.offers[1].amount : 500000)}"></label>
    <div id="cp-gov-quote" class="cp-quote"></div>
    <div class="cp-go"><button type="button" class="primary" id="cp-gov-go" disabled>Make the offer</button></div>`}

    <h4 class="cp-h">Grow in ${esc(d.country)}</h4>
    <div class="cp-moves">
      ${d.is_hq ? "" : `<div class="cp-move"><b>🏢 Expand local operations</b><p>Offices, a warehouse, a local team. More of your international sales go through ${esc(d.country)}.</p>
        <label class="iv-slide"><b data-amt="cp-expand"></b><input type="range" data-slide="cp-expand" min="50000" max="${slideMax(0.6, 100000)}" step="10000" value="${Math.min(slideMax(0.6, 100000), 500000)}"></label>
        <button type="button" data-do="cp_expand">Invest</button></div>
      <div class="cp-move"><b>📣 Local marketing push</b><p>Six months of advertising: a bigger share of your sales here, and some brand.</p>
        <label class="iv-slide"><b data-amt="cp-mkt"></b><input type="range" data-slide="cp-mkt" min="20000" max="${slideMax(0.3, 40000)}" step="10000" value="${Math.min(slideMax(0.3, 40000), 150000)}"></label>
        <button type="button" data-do="cp_marketing">Launch campaign</button></div>
      <div class="cp-move"><b>👤 Hire a country manager</b><p>Someone who knows ${esc(d.country)}: +20% share of sales, more trust. ${cpM(d.manager_salary)} a month for 24 months.</p>
        <button type="button" data-do="cp_manager" ${d.manager_name ? "disabled" : ""}>${d.manager_name ? `${esc(d.manager_name)} is in post` : "Hire"}</button></div>
      <div class="cp-move"><b>🏭 Build a local plant</b><p>Make things where you sell them${d.plant_what ? ` (${esc(d.plant_what)}, sized to your sales here)` : ""}: local-content rules met, and the ${esc(d.currency)} swing on these sales halves.</p>
        <button type="button" data-do="gov_local_plant" ${d.plant ? "disabled" : ""}>${d.plant ? "Plant built" : `Build (${cpM(d.plant_cost || 0)})`}</button></div>`}
      <div class="cp-move"><b>🤝 Fund local CSR</b><p>Schools, clinics, water or skills: trust with the government rises, and your brand and ESG with it.</p>
        <select data-csr-cause>${Object.entries(d.causes).map(([k, v]) => `<option value="${k}">${esc(v)}</option>`).join("")}</select>
        <label class="iv-slide"><b data-amt="cp-csr"></b><input type="range" data-slide="cp-csr" min="10000" max="${slideMax(0.2, 20000)}" step="5000" value="${Math.min(slideMax(0.2, 20000), 50000)}"></label>
        <button type="button" data-do="gov_csr">Fund it</button></div>
      <div class="cp-move"><b>📦 Localize a product</b><p>Language, packaging, local rules: a localized product sells more here (${cpM(d.localize_cost)}).</p>
        ${d.not_localized.length ? `<select data-loc>${d.not_localized.map((p) => `<option>${esc(p)}</option>`).join("")}</select><button type="button" data-do="localize_product">Localize</button>`
          : `<button type="button" disabled>Every product is localized</button>`}</div>
      ${d.is_hq || d.currency === "USD" ? "" : `<div class="cp-move"><b>🛡 Hedge the ${esc(d.currency)}</b><p>Lock today's rate so a currency fall doesn't eat what you earn here.</p>
        <select data-hedge-months>${[3, 6, 12].map((m) => `<option value="${m}">${m} months</option>`).join("")}</select>
        <button type="button" data-do="hedge_currency" ${d.hedged ? "disabled" : ""}>${d.hedged ? "Already hedged" : "Hedge"}</button></div>`}
      ${d.is_hq ? "" : `<div class="cp-move"><b>✈ Visit the government</b><p>Meet a minister for a new incentive. It opens a thread in Government Relations.</p>
        <button type="button" data-do="visit_country_leader">Travel and meet</button></div>
      <div class="cp-move danger"><b>🚪 Leave ${esc(d.country)}</b><p>Stop selling here. What you built stays on your books; trust falls.</p>
        <button type="button" class="danger" data-do="cp_exit">Leave the market</button></div>`}
    </div>

    ${d.feed.length ? `<h4 class="cp-h">What you've done here</h4><ul class="iv-feed">${d.feed.map((f) => `<li><i>M${f.month}</i>${esc(f.type.replace(/_/g, " "))}${f.amount ? ` · ${cpM(f.amount)}` : ""}</li>`).join("")}</ul>` : ""}`;
}

async function cpWireDetail(host, d) {
  const refresh = async (ok) => { if (ok) await window.refreshAll(); };
  host.querySelectorAll("[data-slide]").forEach((r) => {
    const out = host.querySelector(`[data-amt="${r.dataset.slide}"]`);
    const paint = () => { out.textContent = cpM(+r.value); };
    r.addEventListener("input", paint); paint();
  });
  const val = (k) => +host.querySelector(`[data-slide="${k}"]`).value;
  host.querySelectorAll("[data-do]").forEach((b) => b.addEventListener("click", async () => {
    const k = b.dataset.do, c = d.country;
    let ok = false;
    b.disabled = true;
    if (k === "cp_expand") ok = await cpExec(k, { country: c, amount: val("cp-expand") });
    else if (k === "cp_marketing") ok = await cpExec(k, { country: c, amount: val("cp-mkt") });
    else if (k === "cp_manager") ok = confirm(`Hire a country manager for ${c} at ${cpM(d.manager_salary)} a month for 24 months?`) && await cpExec(k, { country: c });
    else if (k === "gov_local_plant") ok = confirm(`Build ${d.plant_what || "a local plant"} in ${c} for ${cpM(d.plant_cost)}?`) && await cpExec(k, { country: c });
    else if (k === "gov_csr") ok = await cpExec(k, { country: c, cause: host.querySelector("[data-csr-cause]").value, amount: val("cp-csr") });
    else if (k === "localize_product") ok = await cpExec(k, { product_name: host.querySelector("[data-loc]").value, country_name: c });
    else if (k === "hedge_currency") ok = await cpExec(k, { currency: d.currency, months: +host.querySelector("[data-hedge-months]").value });
    else if (k === "visit_country_leader") ok = await cpExec(k, { country_name: c });
    else if (k === "cp_exit") ok = confirm(`Leave ${c}? Its sales stop, and trust there falls.`) && await cpExec(k, { country: c });
    if (!ok) { b.disabled = false; return; }
    if (k === "cp_exit") cpSel = null;
    await refresh(ok);
  }));

  // the government's offer, live as you slide
  const range = host.querySelector("#cp-gov");
  if (!range) return;
  const box = host.querySelector("#cp-gov-quote"), go = host.querySelector("#cp-gov-go");
  // 2026-10-24: real pledges are delivered over years (a share now, then quarterly payments)
  box.insertAdjacentHTML("beforebegin", `<label class="iv-slide">Deliver it over <b id="cp-gov-yrs-v">2 years</b>
    <input type="range" id="cp-gov-yrs" min="1" max="4" step="1" value="2" aria-label="Years to deliver the investment"></label>`);
  const yrs = host.querySelector("#cp-gov-yrs");
  let timer = 0, last = null;
  const quote = () => {
    host.querySelector("#cp-gov-amt").textContent = cpM(+range.value);
    host.querySelector("#cp-gov-yrs-v").textContent = `${yrs.value} year${+yrs.value > 1 ? "s" : ""}`;
    clearTimeout(timer);
    timer = setTimeout(async () => {
      let q;
      try { q = await uiGet(`/investments/country-quote?country=${encodeURIComponent(d.country)}&amount=${range.value}&years=${yrs.value}`); }
      catch (e) { box.innerHTML = `<p class="iv-warn">${esc(e.message)}</p>`; go.disabled = true; return; }
      last = q;
      box.innerHTML = q.tier
        ? `<p>🏛 <b>${esc(q.tier)} tier:</b> ${esc(q.official)} offers <b>${esc(q.incentive_words)}</b>. ${Math.round(q.probability * 100)}% chance of the full package.</p>
           <p class="cp-quote-pay">${cpM(q.upfront)} on signing, then ${q.instalments} × ${cpM(q.instalment)} every quarter for ${q.years} year${q.years > 1 ? "s" : ""}.${q.next_tier ? ` Invest ${cpM(q.next_tier.at)} for the ${esc(q.next_tier.label)} tier.` : ""}</p>
           ${q.capped ? `<p class="iv-warn">${esc(d.country)} can absorb about ${cpM(q.max_pledge)} over ${q.years} year${q.years > 1 ? "s" : ""}: the offer is capped there.</p>` : ""}
           ${q.cash < q.upfront ? `<p class="iv-warn">You need ${cpM(q.upfront)} in cash to sign.</p>` : ""}`
        : `<p class="iv-warn">Too small for an incentive: start at ${cpM(q.tiers[0].from)}.</p>`;
      go.disabled = !q.tier || q.cash < q.upfront;
      go.textContent = q.tier ? `Invest ${cpM(q.amount)} in ${d.country}` : "Make the offer";
    }, 200);
  };
  range.addEventListener("input", quote);
  yrs.addEventListener("input", quote);
  host.querySelectorAll("[data-tier]").forEach((t) => t.addEventListener("click", () => {
    range.value = Math.min(+range.max, +t.dataset.tier);
    quote();
  }));
  quote();
  go.addEventListener("click", async () => {
    if (!last) return;
    go.disabled = true;
    if (await cpExec("ci_country_invest", { country_name: last.country, amount: last.amount, years: last.years })) await window.refreshAll();
    else go.disabled = false;
  });
}

window.renderCountries = async function (main) {
  if (window.__ghOpen) ghSeg = "companies";
  if (window.__cpSelect) ghSeg = "countries";
  if (window.__ghSeg) { ghSeg = window.__ghSeg; window.__ghSeg = null; }
  const segs = `<div class="gh-segs" role="tablist">${GH_SEGS.map(([k, l]) => `<button type="button" role="tab" class="gh-seg ${ghSeg === k ? "on" : ""}" aria-selected="${ghSeg === k}" data-seg="${k}">${l}</button>`).join("")}</div>`;
  const wireSegs = () => main.querySelectorAll("[data-seg]").forEach((b) => b.addEventListener("click", () => { ghSeg = b.dataset.seg; window.renderCountries(main); }));
  if (ghSeg === "companies") {
    main.innerHTML = `<div class="sx cp gh-root">
      ${uiHeader("Global Holdings", "Every company you own a piece of: bought in M&A, stakes from Corporate Investments, startups from the Tank. Open one for every move it allows.")}
      ${segs}<div id="gh-companies"><p class="iv-thinking">Gathering your companies…</p></div></div>`;
    wireSegs();
    const host = main.querySelector("#gh-companies");
    try { await window.ghCompanies(host); }
    catch (e) { console.error("Companies you own:", e); host.innerHTML = `<p class="iv-warn">Couldn't load your companies: ${esc(e.message)}</p>`; }
    return;
  }
  const v = await uiGet("/country-portfolio");
  if (window.__cpSelect) { cpSel = window.__cpSelect; window.__cpSelect = null; }
  if (!v.countries.some((r) => r.country === cpSel)) {
    cpSel = v.just_signed[0] || (v.countries.find((r) => !r.is_hq) || v.countries[0] || {}).country || null;
  }
  const s = v.summary;
  const fresh = v.countries.filter((r) => r.just_signed);
  main.innerHTML = `
    <div class="sx cp gh-root">
      ${uiHeader("Global Holdings", "Every country you operate in, as an investment: what you put in, what comes out, and your next move.")}
      ${segs}
      ${uiKpis([
        { label: "Countries", value: s.countries },
        { label: "Sales abroad", value: cpM(s.foreign_revenue), sub: `${s.foreign_share_pct}% of revenue, a month` },
        { label: "Invested abroad", value: cpM(s.invested) },
        { label: "Profit after tax abroad", value: cpM(s.profit_after_tax), tone: s.profit_after_tax >= 0 ? "pos" : "neg", sub: "a month" },
        { label: "Return on it", value: cpPct(s.roi_pct, 0), sub: "a year" },
        { label: "Still owed to governments", value: cpM(s.due), tone: s.due > v.cash ? "neg" : "" },
      ])}
      ${fresh.length ? `<div class="cp-fresh">✍ Just signed with <b>${fresh.map((r) => esc(r.country)).join(", ")}</b>. See what it earns, and decide whether to invest more.</div>` : ""}
      <div class="cp-desk">
        <aside class="cp-list">${v.countries.map((r) => cpRow(r, r.country === cpSel)).join("")}
          <p class="cp-how">ⓘ ${esc(v.how)}</p>
          <button type="button" class="cp-new" id="cp-new">🌍 Enter a new country</button></aside>
        <section class="cp-read" id="cp-read"><p class="iv-thinking">Opening ${esc(cpSel || "")}…</p></section>
      </div>
    </div>`;
  wireSegs();
  main.querySelectorAll("[data-cp]").forEach((b) => b.addEventListener("click", () => { cpSel = b.dataset.cp; window.renderCountries(main); }));
  main.querySelector("#cp-new").addEventListener("click", () => window.ges && window.ges.showTab("government"));
  const read = main.querySelector("#cp-read");
  if (!cpSel) { read.innerHTML = `<p class="iv-none">No countries yet.</p>`; return; }
  let d;
  try { d = await uiGet(`/country-portfolio/${encodeURIComponent(cpSel)}`); }
  catch (e) { read.innerHTML = `<p class="iv-warn">${esc(e.message)}</p>`; return; }
  try {
    read.innerHTML = cpDetail(d);
    await cpWireDetail(read, d);
  } catch (e) {
    console.error("Global Holdings, Countries:", e);
    read.innerHTML = `<p class="iv-warn">Couldn't show ${esc(cpSel)}: ${esc(e.message)}</p>`;
  }
};
